import React, { useEffect, useState } from 'react';
import { SelectValue } from 'antd/lib/select';

import './index.less';

import IbButton from '../../../../components/common/IbButton';
import IbModal, { IbModalPosition } from '../../../../components/common/IbModal';
import IbTypography from '../../../../components/common/IbTypography';
import IbInput from '../../../../components/common/IbInput';
import { CheckFieldValueFnMap, useValidationForm } from '../../../../utils/validationUtil';
import { ValidationErrors } from '../../../../types';
import {
  ASRProviderCreationRequest,
  ASRProviderModel,
  ASRProviderSettingsType,
  ASRProviderUpdatingRequest,
  LLMASRProviderSettings,
  LLMSettings,
  OpenAICompatibleASRProviderSettings,
  OpenAICompatibleASRResponseFormat,
  ProviderModel,
  Settings,
} from '../../../../../gamma-api';
import { getTranslateFunc } from '../../../../utils/stringUtil';
import IbSelect from '../../../../components/common/IbSelect';
import ModelSelector from '../../../../components/ModelSelector';
import PromptEditorExpandable from '../../../../components/common/PromptEditorExpandable';
import { PromptLanguage } from '../../../../components/common/PromptEditor/const';
import { CodeEditorSize } from '../../../../components/common/CodeEditorExpandable/types';

import {
  CONTENT_CLASS_NAME,
  DEFAULT_ASR_PROVIDER,
  EMPTY_AUDIO_LLM_ASR_PROVIDER,
  EMPTY_OPENAI_COMPATIBLE_ASR_PROVIDER,
  FORM_ITEM_CLASS_NAME,
  MAIN_CLASS_NAME,
} from './const';

type AsrProviderForm = {
  name: string;
  type: string;
  model_name: string;
  base_url: string;
  api_key: string;
  model: string;
};

const checkFieldValueFnMap: CheckFieldValueFnMap<AsrProviderForm> = {
  name: (value: string) => !!value,
  type: (value: string) => !!value,
  model_name: (value: string, form: AsrProviderForm) => form.type !== ASRProviderSettingsType.Llm || !!value,
  base_url: (value: string, form: AsrProviderForm) => form.type !== ASRProviderSettingsType.OpenAiCompatible || !!value,
  api_key: (value: string, form: AsrProviderForm) => form.type !== ASRProviderSettingsType.OpenAiCompatible || !!value,
  model: (value: string, form: AsrProviderForm) => form.type !== ASRProviderSettingsType.OpenAiCompatible || !!value,
};

interface IAsrProviderModalProps {
  visible: boolean;
  llmProviders: ProviderModel[];
  llmProvidersLoading: boolean;
  editAsrProvider?: ASRProviderModel;
  onCreate: (
    asrProvider: ASRProviderCreationRequest,
    onSubmitErrors: (errors: ValidationErrors) => void
  ) => Promise<void>;
  onUpdate: (
    asrProvider: ASRProviderUpdatingRequest,
    onSubmitErrors: (errors: ValidationErrors) => void
  ) => Promise<void>;
  onCancel: () => void;
}

const AsrProviderModal: React.FC<IAsrProviderModalProps> = ({
  visible,
  llmProviders,
  llmProvidersLoading,
  editAsrProvider,
  onCreate,
  onUpdate,
  onCancel,
}) => {
  const [saving, setSaving] = useState(false);

  const [asrProvider, setAsrProvider] = useState<ASRProviderModel>(DEFAULT_ASR_PROVIDER);

  const translate = getTranslateFunc('ASR: Provider: Modal:');

  const initialFormValues: AsrProviderForm = {
    name: editAsrProvider?.name ?? '',
    type: editAsrProvider?.settings.type ?? DEFAULT_ASR_PROVIDER.settings.type,
    model_name: (editAsrProvider?.settings as LLMASRProviderSettings)?.llm_settings?.model_name ?? '',
    base_url: (editAsrProvider?.settings as OpenAICompatibleASRProviderSettings)?.base_url ?? '',
    api_key: (editAsrProvider?.settings as OpenAICompatibleASRProviderSettings)?.api_key ?? '',
    model: (editAsrProvider?.settings as OpenAICompatibleASRProviderSettings)?.model ?? '',
  };

  const {
    validationState,
    onFieldChange,
    onFieldBlur,
    onSubmitErrors,
    validate,
    reset,
    getShowError,
    getErrorMessage,
  } = useValidationForm<AsrProviderForm>(initialFormValues, checkFieldValueFnMap);

  const onVisiblePropChange = () => {
    if (!visible) return;

    const newAsrProvider = editAsrProvider ?? DEFAULT_ASR_PROVIDER;

    setSaving(false);
    setAsrProvider(newAsrProvider);
    reset();
  };
  useEffect(onVisiblePropChange, [visible]);

  const onNameChange = (value: string) => {
    setAsrProvider({ ...asrProvider, name: value });
    onFieldChange('name', value);
  };

  const onNameBlur = () => onFieldBlur('name');

  const onTypeChange = (value: SelectValue) => {
    const type = value as ASRProviderSettingsType;
    let newAsrProvider: ASRProviderModel;

    if (type === ASRProviderSettingsType.Llm) {
      newAsrProvider = {
        ...asrProvider,
        settings: { ...EMPTY_AUDIO_LLM_ASR_PROVIDER.settings, ...asrProvider.settings, type } as Settings,
      };
    } else if (type === ASRProviderSettingsType.OpenAiCompatible) {
      newAsrProvider = {
        ...asrProvider,
        settings: { ...EMPTY_OPENAI_COMPATIBLE_ASR_PROVIDER.settings, ...asrProvider.settings, type } as Settings,
      };
    } else {
      newAsrProvider = { ...asrProvider, settings: { ...asrProvider.settings, type } as Settings };
    }

    setAsrProvider(newAsrProvider);
    onFieldChange('type', type);
  };

  const onTypeBlur = () => onFieldBlur('type');

  const onLlmSettingsChange = (value: LLMSettings) => {
    setAsrProvider({ ...asrProvider, settings: { ...asrProvider.settings, llm_settings: value } as Settings });
    onFieldChange('model_name', value.model_name ?? '');
  };

  const onLlmSettingsBlur = () => onFieldBlur('model_name');

  const onDefaultPromptChange = (value: string) => {
    setAsrProvider({ ...asrProvider, settings: { ...asrProvider.settings, default_prompt: value } as Settings });
  };

  const onBaseUrlChange = (value: string) => {
    setAsrProvider({ ...asrProvider, settings: { ...asrProvider.settings, base_url: value } as Settings });
    onFieldChange('base_url', value);
  };

  const onBaseUrlBlur = () => onFieldBlur('base_url');

  const onApiKeyChange = (value: string) => {
    setAsrProvider({ ...asrProvider, settings: { ...asrProvider.settings, api_key: value } as Settings });
    onFieldChange('api_key', value);
  };

  const onApiKeyBlur = () => onFieldBlur('api_key');

  const onModelChange = (value: string) => {
    setAsrProvider({ ...asrProvider, settings: { ...asrProvider.settings, model: value } as Settings });
    onFieldChange('model', value);
  };

  const onModelBlur = () => onFieldBlur('model');

  const onResponseFormatChange = (value: SelectValue) => {
    setAsrProvider({
      ...asrProvider,
      settings: { ...asrProvider.settings, response_format: value as OpenAICompatibleASRResponseFormat } as Settings,
    });
  };

  const onCreateButtonClick = async () => {
    if (!validate()) return;

    setSaving(true);
    await onCreate(asrProvider, onSubmitErrors);
    setSaving(false);
  };

  const onUpdateButtonClick = async () => {
    if (!editAsrProvider || !validate()) return;

    setSaving(true);
    await onUpdate(asrProvider, onSubmitErrors);
    setSaving(false);
  };

  const renderLlmSettings = () => {
    const settings = asrProvider.settings as LLMASRProviderSettings;

    return (
      <>
        <div className={FORM_ITEM_CLASS_NAME}>
          <IbTypography.Paragraph strong type="secondary">
            {translate('LLM settings: Title')}
          </IbTypography.Paragraph>
          <IbTypography.Paragraph type="secondary">{translate('LLM settings: Description')}</IbTypography.Paragraph>
          <ModelSelector.LLM
            loading={llmProvidersLoading}
            providers={llmProviders}
            settings={settings.llm_settings}
            onBlur={onLlmSettingsBlur}
            onChange={onLlmSettingsChange}
          />
          {getShowError(validationState.model_name) && (
            <IbTypography.Paragraph error type="descriptor">
              {getErrorMessage(validationState.model_name, translate('LLM settings: Error'))}
            </IbTypography.Paragraph>
          )}
        </div>
        <div className={FORM_ITEM_CLASS_NAME}>
          <IbTypography.Paragraph strong type="secondary">
            {translate('Default prompt: Title')}
          </IbTypography.Paragraph>
          <IbTypography.Paragraph type="secondary">{translate('Default prompt: Description')}</IbTypography.Paragraph>
          <PromptEditorExpandable
            promptLanguage={PromptLanguage.PLAIN_TEXT}
            size={CodeEditorSize.MD}
            value={settings.default_prompt}
            onChange={onDefaultPromptChange}
          />
        </div>
      </>
    );
  };

  const renderOpenAICompatibleSettings = () => {
    const settings = asrProvider.settings as OpenAICompatibleASRProviderSettings;

    return (
      <>
        <div className={FORM_ITEM_CLASS_NAME}>
          <IbTypography.Paragraph strong type="secondary">
            {translate('Base URL: Title')}
          </IbTypography.Paragraph>
          <IbInput
            status={getShowError(validationState.base_url) ? 'error' : 'default'}
            value={settings.base_url}
            onBlur={onBaseUrlBlur}
            onChange={onBaseUrlChange}
          />
          {getShowError(validationState.base_url) && (
            <IbTypography.Paragraph error type="descriptor">
              {getErrorMessage(validationState.base_url, translate('Base URL: Error'))}
            </IbTypography.Paragraph>
          )}
        </div>
        <div className={FORM_ITEM_CLASS_NAME}>
          <IbTypography.Paragraph strong type="secondary">
            {translate('API Key: Title')}
          </IbTypography.Paragraph>
          <IbInput
            status={getShowError(validationState.api_key) ? 'error' : 'default'}
            value={settings.api_key}
            onBlur={onApiKeyBlur}
            onChange={onApiKeyChange}
          />
          {getShowError(validationState.api_key) && (
            <IbTypography.Paragraph error type="descriptor">
              {getErrorMessage(validationState.api_key, translate('API Key: Error'))}
            </IbTypography.Paragraph>
          )}
        </div>
        <div className={FORM_ITEM_CLASS_NAME}>
          <IbTypography.Paragraph strong type="secondary">
            {translate('Model: Title')}
          </IbTypography.Paragraph>
          <IbInput
            status={getShowError(validationState.model) ? 'error' : 'default'}
            value={settings.model}
            onBlur={onModelBlur}
            onChange={onModelChange}
          />
          {getShowError(validationState.model) && (
            <IbTypography.Paragraph error type="descriptor">
              {getErrorMessage(validationState.model, translate('Model: Error'))}
            </IbTypography.Paragraph>
          )}
        </div>
        <div className={FORM_ITEM_CLASS_NAME}>
          <IbTypography.Paragraph strong type="secondary">
            {translate('Response format: Title')}
          </IbTypography.Paragraph>
          <IbSelect
            options={[
              { value: OpenAICompatibleASRResponseFormat.Text, label: translate('Response format: Value: Text') },
              { value: OpenAICompatibleASRResponseFormat.Srt, label: translate('Response format: Value: Srt') },
              { value: OpenAICompatibleASRResponseFormat.Vtt, label: translate('Response format: Value: Vtt') },
              { value: OpenAICompatibleASRResponseFormat.Json, label: translate('Response format: Value: Json') },
              {
                value: OpenAICompatibleASRResponseFormat.VerboseJson,
                label: translate('Response format: Value: VerboseJson'),
              },
              {
                value: OpenAICompatibleASRResponseFormat.DiarizedJson,
                label: translate('Response format: Value: DiarizedJson'),
              },
            ]}
            value={settings.response_format}
            onChange={onResponseFormatChange}
          />
        </div>
      </>
    );
  };

  const renderSettings = () => {
    if (asrProvider.settings.type === ASRProviderSettingsType.Llm) {
      return renderLlmSettings();
    }

    if (asrProvider.settings.type === ASRProviderSettingsType.OpenAiCompatible) {
      return renderOpenAICompatibleSettings();
    }

    return null;
  };

  const renderContent = () => {
    const options = [
      { value: ASRProviderSettingsType.Llm, label: translate('Type: Value: LLM') },
      { value: ASRProviderSettingsType.OpenAiCompatible, label: translate('Type: Value: OpenAI compatible') },
    ];

    return (
      <div className={CONTENT_CLASS_NAME}>
        <div className={FORM_ITEM_CLASS_NAME}>
          <IbTypography.Paragraph strong type="secondary">
            {translate('Name: Title')}
          </IbTypography.Paragraph>
          <IbInput
            status={getShowError(validationState.name) ? 'error' : 'default'}
            value={asrProvider.name}
            onBlur={onNameBlur}
            onChange={onNameChange}
          />
          {getShowError(validationState.name) && (
            <IbTypography.Paragraph error type="descriptor">
              {getErrorMessage(validationState.name, translate('Name: Error'))}
            </IbTypography.Paragraph>
          )}
        </div>
        <div className={FORM_ITEM_CLASS_NAME}>
          <IbTypography.Paragraph strong type="secondary">
            {translate('Type: Title')}
          </IbTypography.Paragraph>
          <IbSelect
            options={options}
            status={getShowError(validationState.type) ? 'error' : 'default'}
            value={asrProvider.settings.type}
            onBlur={onTypeBlur}
            onChange={onTypeChange}
          />
          {getShowError(validationState.type) && (
            <IbTypography.Paragraph error type="descriptor">
              {getErrorMessage(validationState.type, translate('Type: Error'))}
            </IbTypography.Paragraph>
          )}
        </div>
        {renderSettings()}
      </div>
    );
  };

  const renderFooter = () => {
    return (
      <>
        {editAsrProvider ? (
          <IbButton type="primary" onClick={onUpdateButtonClick}>
            {translate('Button: Update')}
          </IbButton>
        ) : (
          <IbButton type="primary" onClick={onCreateButtonClick}>
            {translate('Button: Create')}
          </IbButton>
        )}
        <IbButton type="secondary" onClick={onCancel}>
          {translate('Button: Cancel')}
        </IbButton>
      </>
    );
  };

  const title = editAsrProvider ? translate('Title: Update') : translate('Title: Create');

  return (
    <IbModal
      className={MAIN_CLASS_NAME}
      footer={renderFooter()}
      loading={saving}
      position={IbModalPosition.Centered}
      title={title}
      visible={visible}
      onCancel={onCancel}
    >
      {renderContent()}
    </IbModal>
  );
};

export default AsrProviderModal;
